import React from 'react'
import PropTypes from 'prop-types'
import {
	LineChart as RechartsLineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer
} from 'recharts'
import './LineChart.scss'

/**
 * LineChart - Responsive line chart with multiple lines support
 *
 * @param {Array} data - Data array for the chart
 * @param {Array} lines - Line configurations [{dataKey, color, name, strokeWidth}]
 * @param {string} xAxisKey - Key for x-axis data
 * @param {number} height - Chart height in pixels
 * @param {boolean} showGrid - Show grid lines
 * @param {boolean} showLegend - Show legend
 * @param {boolean} showTooltip - Show tooltip on hover
 * @param {string} title - Chart title
 * @param {boolean} loading - Show loading state
 */
const LineChart = ({
	data = [],
	lines = [],
	xAxisKey = 'name',
	height = 300,
	showGrid = true,
	showLegend = true,
	showTooltip = true,
	title,
	loading = false
}) => {
	const defaultColors = [
		'var(--color-primary)',
		'var(--color-success)',
		'var(--color-warning)',
		'var(--color-danger)',
		'var(--color-info, #6366f1)'
	]

	const CustomTooltip = ({ active, payload, label }) => {
		if (active && payload && payload.length) {
			return (
				<div className="line-chart__tooltip">
					<p className="line-chart__tooltip-label">{label}</p>
					{payload.map((entry, index) => (
						<p
							key={index}
							className="line-chart__tooltip-value"
							style={{ color: entry.color }}
						>
							{entry.name}: {entry.value}
						</p>
					))}
				</div>
			)
		}
		return null
	}

	if (loading) {
		return (
			<div className="line-chart">
				{title && <h3 className="line-chart__title">{title}</h3>}
				<div className="line-chart__skeleton" style={{ height }}>
					<div className="skeleton skeleton--chart"></div>
				</div>
			</div>
		)
	}

	if (data.length === 0) {
		return (
			<div className="line-chart">
				{title && <h3 className="line-chart__title">{title}</h3>}
				<div className="line-chart__empty" style={{ height }}>
					<p>No data available</p>
				</div>
			</div>
		)
	}

	return (
		<div className="line-chart">
			{title && <h3 className="line-chart__title">{title}</h3>}
			<div className="line-chart__container">
				<ResponsiveContainer width="100%" height={height}>
					<RechartsLineChart
						data={data}
						margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
					>
						{showGrid && (
							<CartesianGrid
								strokeDasharray="3 3"
								stroke="var(--color-gray-200)"
								vertical={false}
							/>
						)}
						<XAxis
							dataKey={xAxisKey}
							tick={{ fontSize: 12, fill: 'var(--color-gray-500)' }}
							tickLine={false}
							axisLine={{ stroke: 'var(--color-gray-200)' }}
						/>
						<YAxis
							tick={{ fontSize: 12, fill: 'var(--color-gray-500)' }}
							tickLine={false}
							axisLine={false}
							width={40}
						/>
						{showTooltip && <Tooltip content={<CustomTooltip />} />}
						{showLegend && (
							<Legend
								wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
								iconType="circle"
								iconSize={8}
							/>
						)}
						{lines.map((line, index) => (
							<Line
								key={line.dataKey}
								type="monotone"
								dataKey={line.dataKey}
								name={line.name || line.dataKey}
								stroke={line.color || defaultColors[index % defaultColors.length]}
								strokeWidth={line.strokeWidth || 2}
								dot={{ r: 3, fill: line.color || defaultColors[index % defaultColors.length] }}
								activeDot={{ r: 5 }}
							/>
						))}
					</RechartsLineChart>
				</ResponsiveContainer>
			</div>
		</div>
	)
}

LineChart.propTypes = {
	data: PropTypes.arrayOf(PropTypes.object),
	lines: PropTypes.arrayOf(
		PropTypes.shape({
			dataKey: PropTypes.string.isRequired,
			color: PropTypes.string,
			name: PropTypes.string,
			strokeWidth: PropTypes.number
		})
	),
	xAxisKey: PropTypes.string,
	height: PropTypes.number,
	showGrid: PropTypes.bool,
	showLegend: PropTypes.bool,
	showTooltip: PropTypes.bool,
	title: PropTypes.string,
	loading: PropTypes.bool
}

export default LineChart
